import { forwardRef } from 'react'
import { Button, type ButtonProps } from '@/shared/components/ui/button'
import { useAuthStore } from '@/shared/stores/auth.store'
import { hasPerm, RbacAction, RbacScope, type ModuleKey, type ActionKey, type ScopeKey } from './permissions'

/**
 * Canonical "mutating action" button.
 *
 * Wraps the base <Button> with an RBAC capability check. If the current user
 * lacks the required permission (typically `edit` on the relevant module),
 * the button renders **disabled with a tooltip** rather than being hidden —
 * read-only users still see what actions exist on the page, they just can't
 * trigger them.
 *
 * This is the disabled-vs-hidden convention agreed during the RBAC refactor:
 *   • read access  → page loads, action buttons disabled + tooltip
 *   • edit access  → action buttons active
 *
 * Usage — most common form (defaults to `edit` × `own`):
 *
 *   <ActionButton requires="chart_queue" onClick={handleBulkAssign}>
 *     Bulk Assign
 *   </ActionButton>
 *
 * Stricter scope when the action genuinely needs cross-client privilege:
 *
 *   <ActionButton requires={{ module: 'history', scope: 'all' }} onClick={handleHardDelete}>
 *     Permanently delete
 *   </ActionButton>
 *
 * `requires` accepts either a module slug (string) — implying
 * `(action='edit', scope='own')` — or an object with explicit fields.
 */

export interface ActionRequirement {
  module: ModuleKey
  action?: ActionKey
  scope?: ScopeKey
}

export interface ActionButtonProps extends Omit<ButtonProps, 'title'> {
  requires: ModuleKey | ActionRequirement
  /** Overrides the default disabled tooltip. */
  noAccessTooltip?: string
  /** Custom title shown when the user IS allowed (passes through to <button>). */
  title?: string
}

function normalize(req: ModuleKey | ActionRequirement): Required<ActionRequirement> {
  if (typeof req === 'string') {
    return { module: req, action: RbacAction.Edit, scope: RbacScope.Own }
  }
  return {
    module: req.module,
    action: req.action ?? RbacAction.Edit,
    scope: req.scope ?? RbacScope.Own,
  }
}

export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  ({ requires, noAccessTooltip, title, disabled, ...rest }, ref) => {
    const user = useAuthStore((s) => s.user)
    const cached = useAuthStore((s) => s.cachedPermissions)
    const perms = user?.permissions ?? cached

    const { module, action, scope } = normalize(requires)
    const allowed = hasPerm(perms, module, action, scope)

    const effectiveDisabled = disabled || !allowed
    const effectiveTitle = !allowed
      ? (noAccessTooltip ?? 'You have read-only access on this module.')
      : title

    return (
      <Button
        ref={ref}
        disabled={effectiveDisabled}
        title={effectiveTitle}
        aria-disabled={effectiveDisabled ? 'true' : undefined}
        {...rest}
      />
    )
  },
)

ActionButton.displayName = 'ActionButton'
